import type Phaser from "phaser";

export interface CameraBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CameraPanInput {
  north: boolean;
  east: boolean;
  south: boolean;
  west: boolean;
}

export interface CameraScrollTarget {
  scrollX: number;
  scrollY: number;
  zoom: number;
  width: number;
  height: number;
}

const MIN_CAMERA_ZOOM = 0.75;
const MAX_CAMERA_ZOOM = 3;
const MAX_AUTO_CAMERA_ZOOM = 2;
const CAMERA_ZOOM_STEP = 0.25;
const TARGET_VISIBLE_TILE_ROWS = 10;
const TARGET_VISIBLE_TILE_COLUMNS = 12;
const SCALE_RESIZE_EVENT = "resize";

export function zoomForViewport(
  width: number,
  height: number,
  tileSize = 32,
): number {
  if (tileSize <= 0) return 1;
  const heightZoom = height / (tileSize * TARGET_VISIBLE_TILE_ROWS);
  const widthZoom = width / (tileSize * TARGET_VISIBLE_TILE_COLUMNS);
  const fittedZoom = Math.min(MAX_AUTO_CAMERA_ZOOM, heightZoom, widthZoom);
  const steppedZoom =
    Math.floor(fittedZoom / CAMERA_ZOOM_STEP) * CAMERA_ZOOM_STEP;
  return Math.max(MIN_CAMERA_ZOOM, steppedZoom);
}

const BROWSER_SCROLL_KEYS = new Set([38, 40, 37, 39, 32]);
const CAMERA_KEY = {
  north: [87, 38],
  east: [68, 39],
  south: [83, 40],
  west: [65, 37],
  center: 32,
} as const;

export function normalizedPanVector(input: CameraPanInput): {
  x: number;
  y: number;
} {
  const x = Number(input.east) - Number(input.west);
  const y = Number(input.south) - Number(input.north);
  const length = Math.hypot(x, y);
  return length > 0 ? { x: x / length, y: y / length } : { x: 0, y: 0 };
}

export function clampCameraScroll(
  camera: CameraScrollTarget,
  bounds: CameraBounds,
): void {
  const zoom = Math.max(0.01, camera.zoom);
  const viewWidth = camera.width / zoom;
  const viewHeight = camera.height / zoom;
  const minX = bounds.x + (viewWidth - camera.width) / 2;
  const minY = bounds.y + (viewHeight - camera.height) / 2;
  const maxX = Math.max(minX, minX + bounds.width - viewWidth);
  const maxY = Math.max(minY, minY + bounds.height - viewHeight);
  camera.scrollX = Math.max(minX, Math.min(maxX, camera.scrollX));
  camera.scrollY = Math.max(minY, Math.min(maxY, camera.scrollY));
}

export function panCamera(
  camera: CameraScrollTarget,
  bounds: CameraBounds,
  input: CameraPanInput,
  deltaMs: number,
  speedScreenPixelsPerSecond: number,
): void {
  const vector = normalizedPanVector(input);
  const elapsedSeconds = Math.max(0, Math.min(deltaMs, 100)) / 1_000;
  const worldDistance =
    (speedScreenPixelsPerSecond * elapsedSeconds) / Math.max(0.01, camera.zoom);
  camera.scrollX += vector.x * worldDistance;
  camera.scrollY += vector.y * worldDistance;
  clampCameraScroll(camera, bounds);
}

function isTypingTarget(): boolean {
  if (typeof document === "undefined") return false;
  const target = document.activeElement;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement &&
      (target.isContentEditable || target.closest("dialog[open]") !== null))
  );
}

export class FreeCameraController {
  private readonly scene: Phaser.Scene;
  private readonly camera: Phaser.Cameras.Scene2D.Camera;
  private readonly bounds: CameraBounds;
  private readonly panSpeed: number;
  private readonly keys: Record<
    "north" | "east" | "south" | "west",
    Phaser.Input.Keyboard.Key[]
  >;
  private readonly centerKey: Phaser.Input.Keyboard.Key;
  private centerWasDown = false;
  private disposed = false;
  private userAdjustedZoom = false;
  private previousWidth: number;
  private previousHeight: number;
  private readonly preventBrowserScroll = (event: KeyboardEvent): void => {
    if (!isTypingTarget() && BROWSER_SCROLL_KEYS.has(event.keyCode))
      event.preventDefault();
  };

  constructor(
    scene: Phaser.Scene,
    options: {
      bounds: CameraBounds;
      initialCenter: { x: number; y: number };
      zoom?: number;
      panSpeedScreenPixelsPerSecond?: number;
    },
  ) {
    this.scene = scene;
    this.camera = scene.cameras.main;
    this.bounds = options.bounds;
    this.panSpeed = options.panSpeedScreenPixelsPerSecond ?? 560;
    this.previousWidth = this.camera.width;
    this.previousHeight = this.camera.height;
    this.camera.setZoom(
      options.zoom ?? zoomForViewport(this.camera.width, this.camera.height),
    );
    this.camera.setBounds(
      this.bounds.x,
      this.bounds.y,
      this.bounds.width,
      this.bounds.height,
    );
    this.camera.roundPixels = true;
    this.centerOn(options.initialCenter);

    const keyboard = scene.input.keyboard;
    if (!keyboard) throw new Error("Gameplay keyboard input is unavailable");
    this.keys = {
      north: CAMERA_KEY.north.map((code) => keyboard.addKey(code, false)),
      east: CAMERA_KEY.east.map((code) => keyboard.addKey(code, false)),
      south: CAMERA_KEY.south.map((code) => keyboard.addKey(code, false)),
      west: CAMERA_KEY.west.map((code) => keyboard.addKey(code, false)),
    };
    this.centerKey = keyboard.addKey(CAMERA_KEY.center, false);
    keyboard.on("keydown", this.preventBrowserScroll, this);
    scene.scale.on(SCALE_RESIZE_EVENT, this.handleResize, this);
    scene.events.once("shutdown", this.dispose, this);
    scene.events.once("destroy", this.dispose, this);
  }

  update(deltaMs: number, centerTarget?: { x: number; y: number }): void {
    if (this.disposed) return;
    const centerDown = this.centerKey.isDown;
    if (centerDown && !this.centerWasDown && centerTarget && !isTypingTarget())
      this.centerOn(centerTarget);
    this.centerWasDown = centerDown;
    if (isTypingTarget()) return;

    const input: CameraPanInput = {
      north: this.keys.north.some((key) => key.isDown),
      east: this.keys.east.some((key) => key.isDown),
      south: this.keys.south.some((key) => key.isDown),
      west: this.keys.west.some((key) => key.isDown),
    };
    panCamera(this.camera, this.bounds, input, deltaMs, this.panSpeed);
  }

  centerOn(point: { x: number; y: number }): void {
    this.camera.centerOn(point.x, point.y);
    clampCameraScroll(this.camera, this.bounds);
  }

  panByScreenDelta(deltaX: number, deltaY: number): void {
    this.camera.scrollX -= deltaX / this.camera.zoom;
    this.camera.scrollY -= deltaY / this.camera.zoom;
    clampCameraScroll(this.camera, this.bounds);
  }

  zoomBy(
    amount: number,
    screenPoint = { x: this.camera.width / 2, y: this.camera.height / 2 },
  ): void {
    this.userAdjustedZoom = true;
    this.setZoomAtScreenPoint(this.camera.zoom + amount, screenPoint);
  }

  zoomByFactor(factor: number, screenPoint: { x: number; y: number }): void {
    if (!Number.isFinite(factor) || factor <= 0) return;
    this.userAdjustedZoom = true;
    this.setZoomAtScreenPoint(this.camera.zoom * factor, screenPoint);
  }

  private setZoomAtScreenPoint(
    requestedZoom: number,
    screenPoint: { x: number; y: number },
  ): void {
    const nextZoom = Math.max(
      MIN_CAMERA_ZOOM,
      Math.min(MAX_CAMERA_ZOOM, requestedZoom),
    );
    if (Math.abs(nextZoom - this.camera.zoom) < 0.001) return;
    const worldPoint = {
      x:
        this.camera.scrollX +
        this.camera.width / 2 +
        (screenPoint.x - this.camera.width / 2) / this.camera.zoom,
      y:
        this.camera.scrollY +
        this.camera.height / 2 +
        (screenPoint.y - this.camera.height / 2) / this.camera.zoom,
    };
    this.camera.setZoom(nextZoom);
    this.camera.scrollX =
      worldPoint.x -
      this.camera.width / 2 -
      (screenPoint.x - this.camera.width / 2) / nextZoom;
    this.camera.scrollY =
      worldPoint.y -
      this.camera.height / 2 -
      (screenPoint.y - this.camera.height / 2) / nextZoom;
    clampCameraScroll(this.camera, this.bounds);
  }

  private handleResize(): void {
    if (this.disposed) return;
    const center = {
      x: this.camera.scrollX + this.previousWidth / 2,
      y: this.camera.scrollY + this.previousHeight / 2,
    };
    this.camera.centerOn(center.x, center.y);
    if (!this.userAdjustedZoom)
      this.camera.setZoom(
        zoomForViewport(this.camera.width, this.camera.height),
      );
    clampCameraScroll(this.camera, this.bounds);
    this.previousWidth = this.camera.width;
    this.previousHeight = this.camera.height;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scene.input.keyboard?.off("keydown", this.preventBrowserScroll, this);
    this.scene.scale.off(SCALE_RESIZE_EVENT, this.handleResize, this);
    this.scene.events.off("shutdown", this.dispose, this);
    this.scene.events.off("destroy", this.dispose, this);
  }
}
